// src/components/IncomingOrders/OrderProductsAndSummation.jsx

import { getISOWeek } from "date-fns";
import PropTypes from "prop-types";
import React, { useMemo, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useIntl, FormattedMessage, FormattedNumber } from "react-intl";
import { Box, Chip, Grid, Table, TableBody, TableCell, TableHead, TableRow, TextField, MenuItem, Tooltip } from "@mui/material";
import { FormattedCurrency, FormattedDate } from "../common";
import IconLink from "../common/FormControl/IconLink";
import useCellSx from "./hooks/useCellSx";
import HeaderCell from "./OrderProductsAndSummation/HeaderCell";
import _get from "lodash/get";
import {
  Alert,
  Button,
  Snackbar,
} from "@mui/material";



// Multi bulk edit (TODO)
//
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
//
// (Multi bulk edit)


import useBulkColumnEdit from "./hooks/useBulkColumnEdit";
import BulkColumnPopover from "./OrderProductsAndSummation/BulkColumnPopover";
import PickedByCell from "./OrderProductsAndSummation/PickedByCell";
import ORDER_STATUSES from "./helpers/orderCategories";

const ColumnKey = Object.freeze({
  DOWNLOAD: "download",
  PRODUCT: "product",
  PACKAGE: "package",
  DELIVERY_DATE_WISH: "deliveryDateWish",
  CONFIRMED_DELIVERY_DATE: "confirmedDeliveryDate",
  SUPPLIER_STATUS: "supplierStatus",
  LOCATION:  "stock_location",
  PICKED_BY: "pickedBy",
  QUANTITY: "quantity",
  PRICE: "price",
  WEIGHT: "weight",
});


// Status chip (TODO)
//
const computeColumnStatus = (rows, field) => {
  const values = rows.map((r) => r?.[field] ?? null);

  const allEmpty = values.every(
    (v) => v === null || v === undefined || v === ""
  );
  if (allEmpty) return { kind: "empty", label: "Empty" };

  const nonEmpty = values.filter(
    (v) => v !== null && v !== undefined && v !== ""
  );

  if (!nonEmpty.length)
    return { kind: "empty", label: "Empty" };

  const first = nonEmpty[0];
  const allSame = nonEmpty.every(
    (v) => v === first
  );

  if (allSame && nonEmpty.length === values.length)
    return { kind: "same", label: "All same" };

  if (allSame)
    return { kind: "partial", label: "Some empty" };

  return { kind: "mixed", label: "Mixed" };
};
//
// (Status chip)


// Status chip (TODO)
//
const getChipColor = (kind) => {
  switch (kind) {
    case "same":
      return "success";
    case "mixed":
      return "warning";
    case "empty":
      return "default";
    case "partial":
      return "info";
    default:
      return "default";
  }
};
//
// (Status chip)


// Normalizes deliveryDateWish to Date | null for DatePicker.
// Accepts null, ISO string, or Date; invalid values return null.
const safeDate = value => {
  if (!value) return null;

  const date =
    value instanceof Date
      ? value
      : new Date(value);

  return Number.isNaN(date.getTime()) ? null : date;
};

const DownloadIconLink = ({ ...props }) => (
  <IconLink
    {...props}
    icon="fa fa-download"
    sx={{
      icon: {
        color: (theme) => theme.palette.common.link,
      },
    }}
  />
);

const OrderProductsAndSummation = ({
  onOpenStatusManager,
  isEditing = false,
  isFieldDirty,
  onDownloadOrderFile,
  supplierOrderStatuses = [],
  orderRows = [],
  showConfirmedDeliveryDateColumn,
  showDeliveryDateWishColumn,
  showDownloadColumn,
  showPackageColumn,
  showPriceColumn,
  showStockLocation,
  showProductColumn,
  showStatusColumn,
  showQuantityColumn,
  showSummation,
  showWeightColumn,
  showPickedByColumn,
  sumExclVat,
  sumInclVat,
  transportFee,
  updateField,
  vatAmounts = [],
  currency = "SEK",
  availableUsers,  
  tableSettings
}) => {
  const intl = useIntl();

  console.count("** OrderProductsAndSummation rendered");

  const cellSx = useCellSx({ isFieldDirty });

  const [undoSnapshot, setUndoSnapshot] = useState(null);
  const [snack, setSnack] = useState({ open: false, text: "" });


  // Multi bulk edit (TODO)
  //
  const [multiOpen, setMultiOpen] = useState(false);
  const [multiMode, setMultiMode] = useState("all");
  const [multiSelected, setMultiSelected] = useState({});
  const [multiValues, setMultiValues] = useState({});
  //
  // (Multi bulk edit)


  const activeColumns = [
    showDownloadColumn && ColumnKey.DOWNLOAD,
    showProductColumn && ColumnKey.PRODUCT,
    showPackageColumn && ColumnKey.PACKAGE,
    showDeliveryDateWishColumn && ColumnKey.DELIVERY_DATE_WISH,
    showConfirmedDeliveryDateColumn && ColumnKey.CONFIRMED_DELIVERY_DATE,
    showStatusColumn && ColumnKey.SUPPLIER_STATUS,
    showStockLocation && ColumnKey.LOCATION,
    showPickedByColumn && ColumnKey.PICKED_BY,
    showQuantityColumn && ColumnKey.QUANTITY,
    showPriceColumn && ColumnKey.PRICE,
    showWeightColumn && ColumnKey.WEIGHT,
  ].filter(Boolean);

  const columnCount = activeColumns.length;

  const datePickerProps = useMemo(
    () => ({
      dateFormat: "yyyy-MM-dd",
      minDate: new Date(),
      selectsStart: true,
      wrapperClassName: "datePicker",
    }),
    []
  );

  const getIsoWeekString = (value) => {
    if (!value) return "";

    const d = new Date(value);

    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");

    const datePart = `${year}-${month}-${day}`;

    const week = getISOWeek(d);

    return `${datePart} (vecka ${week})`;
  };

  const renderSummationRows = () => {
    if (!showSummation) return null;

    const SUMMARY_VALUE_COLUMNS = 2;
    const leadingColumns = columnCount - SUMMARY_VALUE_COLUMNS;

    return (
      <>
        <TableRow>
          <TableCell
            colSpan={leadingColumns}
            rowSpan={(vatAmounts || []).length + 3}
          />
          <TableCell sx={{ borderTop: "2px solid black" }}>
            <FormattedMessage id="orderHistory.freight" />
          </TableCell>
          <TableCell sx={{ textAlign: "right", borderTop: "2px solid black" }}>
            <FormattedCurrency value={transportFee} currency={currency} />
          </TableCell>
        </TableRow>

        <TableRow>
          <TableCell>
            <FormattedMessage id="orderHistory.net" />
          </TableCell>
          <TableCell sx={{ textAlign: "right" }}>
            <FormattedCurrency value={sumExclVat} currency={currency} />
          </TableCell>
        </TableRow>

        {vatAmounts?.map(vatItem => (
          <TableRow key={vatItem.name}>
            <TableCell sx={{ whiteSpace: "nowrap" }}>
              {vatItem.name}
            </TableCell>
            <TableCell sx={{ textAlign: "right" }}>
              <FormattedCurrency value={vatItem.vatAmount} currency={currency} />
            </TableCell>
          </TableRow>
        ))}

        <TableRow>
          <TableCell sx={{ textAlign: "nowrap" }}>
            <FormattedMessage id="orderHistory.total" />
          </TableCell>
          <TableCell sx={{ textAlign: "right", fontWeight: 600 }}>
            <FormattedCurrency value={sumInclVat} currency={currency} />
          </TableCell>
        </TableRow>
      </>
    );
  };

  const OrderTableHeader = ({ activeColumns, onOpenStatusManager }) => {
    const statusInfo = false && /* Status chip (TODO) */ computeColumnStatus(
      orderRows,
      "supplierOrderStatusId"
    );

    return (
      <TableHead>
        <TableRow>
          {activeColumns.includes(ColumnKey.DOWNLOAD) && (
            <HeaderCell sx={{ paddingRight: 0, "@media print": { display: "none" } }} />
          )}
          {activeColumns.includes(ColumnKey.PRODUCT) && (
            <HeaderCell sx={{ paddingLeft: 0 }}>
              <FormattedMessage id="orderHistory.product" />
            </HeaderCell>
          )}
          {activeColumns.includes(ColumnKey.PACKAGE) && (
            <HeaderCell>
              <FormattedMessage id="orderHistory.package" />
            </HeaderCell>
          )}

          {activeColumns.includes(ColumnKey.CONFIRMED_DELIVERY_DATE) && (
            <HeaderCell
              field="deliveryDateWish"
              editAction={{
                onClick: ({ anchorEl, field }) => openBulkEditor({ anchorEl, field }),
                title: "Bulk edit",
              }}
            >
              <FormattedMessage id="orderHistory.deliveryDateWish" />
            </HeaderCell>
          )}

          {activeColumns.includes(ColumnKey.CONFIRMED_DELIVERY_DATE) && (
            <HeaderCell
              field="confirmedDeliveryDate"
              editAction={{
                onClick: ({ anchorEl, field }) => openBulkEditor({ anchorEl, field }),
                title: "Bulk edit",
              }}
            >
              <FormattedMessage id="orderHistory.confirmedDeliveryDate" />
            </HeaderCell>
          )}

          <HeaderCell
            field="supplierOrderStatusId"
            editAction={{
              onClick: ({ anchorEl, field }) => openBulkEditor({ anchorEl, field }),
              title: "Bulk edit",
            }}
            settingsAction={{
              onClick: () => onOpenStatusManager(true),
              title: intl.formatMessage({ id: "incomingOrders.toolbar.settings" }),
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <FormattedMessage id="orderHistory.supplierStatus" />
              {statusInfo && (
                <Chip
                  size="small"
                  label={statusInfo.label}
                  color={getChipColor(statusInfo.kind)}
                  variant="outlined"
                  sx={{
                    height: 18,
                    fontSize: 10,
                    borderRadius: 999,
                  }}
                />
              )}
            </Box>
          </HeaderCell>

          {activeColumns.includes(ColumnKey.LOCATION) && (<HeaderCell><FormattedMessage id="product.location"/></HeaderCell>)}

          {activeColumns.includes(ColumnKey.PICKED_BY) && (
            <HeaderCell
              field="stockInfo.pickedBy"
              editAction={{
                onClick: ({ anchorEl, field }) => openBulkEditor({ anchorEl, field }),
                title: "Bulk edit",
              }}

            ><FormattedMessage id="incomingOrders.handledBy" /></HeaderCell>)}

          {activeColumns.includes(ColumnKey.QUANTITY) && (
            <HeaderCell align="right" sx={{ textAlign: "right" }}>
              <FormattedMessage id="orderHistory.quantity" />
            </HeaderCell>
          )}

          {activeColumns.includes(ColumnKey.WEIGHT) && (
            <HeaderCell align="right" sx={{ textAlign: "right" }}>
              <FormattedMessage id="orderHistory.weight" />
            </HeaderCell>
          )}

          {activeColumns.includes(ColumnKey.PRICE) && (
            <HeaderCell align="right" sx={{ textAlign: "right" }}>
              <FormattedMessage id="orderHistory.price" />
            </HeaderCell>
          )}

        </TableRow>
      </TableHead>
    );
  };


  const statusOrderMap = ORDER_STATUSES.reduce((acc, item, index) => {
    acc[item.key] = index;
    return acc;
  }, {});

  const sortedSupplierOrderStatuses = useMemo(() => {
    return [...supplierOrderStatuses].sort((a, b) => {
      const aIndex = statusOrderMap[a.productionStatus] ?? 999;
      const bIndex = statusOrderMap[b.productionStatus] ?? 999;

      return aIndex - bIndex;
    });
  }, [supplierOrderStatuses, statusOrderMap]);


  const OrderProductRow = React.memo(({
    row,
    activeColumns,
    isEditing,
    datePickerProps,
    updateField,
    onDownloadOrderFile,
    supplierOrderStatuses,
    availableUsers,
  }) => (
    <TableRow>
      {activeColumns.includes(ColumnKey.DOWNLOAD) && (
        <TableCell sx={{ paddingRight: 0, "@media print": { display: "none" } }}>
          <DownloadIconLink onClick={() => onDownloadOrderFile?.(row.id)} />
        </TableCell>
      )}

      {activeColumns.includes(ColumnKey.PRODUCT) && (
        <TableCell sx={{ paddingLeft: 2 }}>{row.product}</TableCell>
      )}

      {activeColumns.includes(ColumnKey.PACKAGE) && <TableCell />}

      {activeColumns.includes(ColumnKey.DELIVERY_DATE_WISH) && (
        <TableCell
          sx={{
            ...cellSx({ fieldName: "deliveryDateWish", rowId: row.id, type: "date" }),
            ...previewSx(row.id, "deliveryDateWish"),
          }}
        >
          {isEditing ? (
            <Tooltip placement="top" title={getIsoWeekString(row.deliveryDateWish)}>
              <span>
                <DatePicker
                  {...datePickerProps}
                  onChange={date => updateField("deliveryDateWish", date, { rowId: row.id, isDate: true })}
                  selected={safeDate(
                    getDisplayValue(row, "deliveryDateWish")
                  )}
                />
              </span>
            </Tooltip>
          ) : (
            <Tooltip placement="top" title={getIsoWeekString(row.deliveryDateWish)}>
              <span>
                <FormattedDate value={row.deliveryDateWish} />
              </span>
            </Tooltip>
          )}
        </TableCell>
      )}

      {activeColumns.includes(ColumnKey.CONFIRMED_DELIVERY_DATE) && (
        <TableCell
          sx={{
            ...cellSx({ fieldName: "confirmedDeliveryDate", rowId: row.id, type: "date" }),
            ...previewSx(row.id, "confirmedDeliveryDate"),
          }}
        >
          {isEditing ? (
            <Tooltip placement="top" title={getIsoWeekString(row.confirmedDeliveryDate)}>
              <span>
                <DatePicker
                  {...datePickerProps}
                    onChange={date => updateField("confirmedDeliveryDate", date, { rowId: row.id, isDate: true })}
                  selected={safeDate(
                    getDisplayValue(row, "confirmedDeliveryDate")
                  )}
                />
              </span>
            </Tooltip>
          ) : (
            <Tooltip placement="top" title={getIsoWeekString(row.deliveryDateWish)}>
              <span>
                <FormattedDate value={row.confirmedDeliveryDate} />
              </span>
            </Tooltip>
          )}
        </TableCell>
      )}

      <TableCell
        sx={{
          ...cellSx({ fieldName: "supplierOrderStatusId", rowId: row.id, type: "select" }),
          ...previewSx(row.id, "supplierOrderStatusId"),
        }}
      >
        {isEditing ? (
          <TextField
            select
            size="small"
            fullWidth
            value={
              getDisplayValue(row, "supplierOrderStatusId") ?? ""
            }
            onChange={(e) => {
              const newId = e.target.value ? Number(e.target.value) : null;
              updateField("supplierOrderStatusId", newId, { rowId: row.id });
            }}
          >
              {sortedSupplierOrderStatuses.map((status) => (
              <MenuItem key={status.id} value={status.id}>
                {status.name}
              </MenuItem>
            ))}
          </TextField>
        ) : (
          (() => {
                const status = sortedSupplierOrderStatuses.find(
              (s) =>
                s.id ===
                getDisplayValue(row, "supplierOrderStatusId")
            );
            return (
              <span
                style={{
                  backgroundColor: status?.color || "transparent",
                  padding: "2px 6px",
                  borderRadius: 4,
                  display: "inline-block",
                }}
              >
                {status?.name || "�"}
              </span>
            );
          })()
        )}
        </TableCell>
        {activeColumns.includes(ColumnKey.LOCATION) && (<TableCell>{row?.stockInfo?.stockLocation}</TableCell>)} 

        {activeColumns.includes(ColumnKey.PICKED_BY) && (
          <PickedByCell
            row={row}
            availableUsers={availableUsers}
            updateField={updateField}
          />)}

      {activeColumns.includes(ColumnKey.QUANTITY) && (
        <TableCell sx={{ textAlign: "right" }}>
          <FormattedNumber value={row.amount} />
        </TableCell>
      )}

        {activeColumns.includes(ColumnKey.WEIGHT) && (
          <TableCell sx={{ textAlign: "right" }}>
            <FormattedNumber style="unit" value={row.weight} unit="gram" />
          </TableCell>
        )}

      {activeColumns.includes(ColumnKey.PRICE) && (
        <TableCell sx={{ textAlign: "right" }}>
          <FormattedCurrency value={row.price} currency={currency} />
        </TableCell>
      )}


    </TableRow>
  ));

  //Bulk edit setup
  const bulkColumnDefs = [
    showDeliveryDateWishColumn && {
      field: "deliveryDateWish",
      type: "date",
    },
    showConfirmedDeliveryDateColumn && {
      field: "confirmedDeliveryDate",
      type: "date",
    },
    {
      field: "stockInfo.pickedBy",
      type: "user",
      editor: ({ value, setValue, inputRef }) => (
        <TextField
          select
          size="small"
          inputRef={inputRef}
          value={value ?? ""}
          onChange={(e) =>
            setValue(e.target.value)
          }
          sx={{ width: 240 }}
        >
          {availableUsers.map((user) => (
            <MenuItem
              key={user.userId}
              value={user.username}
            >
              {user.name}
            </MenuItem>
          ))}
        </TextField>
      ),
    },
    showStatusColumn && {
      field: "supplierOrderStatusId",
      type: "select",
      editor: ({ value, setValue, inputRef }) => (
        <TextField
          select
          size="small"
          inputRef={inputRef}
          value={value ?? ""}
          onChange={(e) =>
            setValue(
              e.target.value
                ? Number(e.target.value)
                : null
            )
          }
          sx={{ width: 200 }}
        >
          {sortedSupplierOrderStatuses.map(
            (status) => (
              <MenuItem
                key={status.id}
                value={status.id}
              >
                {status.name}
              </MenuItem>
            )
          )}
        </TextField>
      ),
    },
  ].filter(Boolean);

  const applyChanges = ({
    field,
    value,
    rowKeys,
  }) => {
    const snapshot = [];

    rowKeys.forEach((rowIdStr) => {
      const row = orderRows.find(
        (r) =>
          String(r.id) === String(rowIdStr)
      );
      if (!row) return;

      snapshot.push({
        rowId: row.id,
        prev: row[field],
      });

      const isDate =
        field === "deliveryDateWish" ||
        field === "confirmedDeliveryDate";

      updateField(field, value, {
        rowId: row.id,
        isDate,
      });
    });

    if (snapshot.length) {
      setUndoSnapshot({ field, rows: snapshot });
      setSnack({
        open: true,
        text: `${snapshot.length} rader uppdaterade`,
      });
    }
  };


  // Multi bulk edit (TODO)
  //
  const applyMultiBulk = () => {
    const selectedFields = Object.entries(
      multiSelected
    )
      .filter(([, v]) => v)
      .map(([k]) => k);

    if (!selectedFields.length) return;

    const rowKeys = orderRows.map((r) => r.id);

    selectedFields.forEach((field) => {
      const value = multiValues[field];

      applyChanges({
        field,
        value,
        rowKeys,
      });
    });
  };
  //
  // (Multi bulk edit)


  const handleUndo = () => {
    if (!undoSnapshot) return;

    undoSnapshot.rows.forEach((r) => {
      updateField(
        undoSnapshot.field,
        r.prev,
        { rowId: r.rowId }
      );
    });

    setUndoSnapshot(null);
    setSnack({ open: false, text: "" });
  };

  const {
    bulk,
    setBulk,
    activeCol,
    previewKeys,
    openBulkEditor,
    closeBulkEditor,
    applySingleBulk,
    onEditorKeyDown,
    inputRefs,
  } = useBulkColumnEdit({
    rows: orderRows,
    columnDefs: bulkColumnDefs,
    getRowKey: (row) => row.id,
    getValue: (row, field) =>
      _get(row, field),
    applyChanges,
  });

  const isPreviewCell = (rowId, field) =>
    bulk.open &&
    bulk.field === field &&
    previewKeys.has(String(rowId));

  const previewSx = (rowId, field) =>
    isPreviewCell(rowId, field)
      ? {
        backgroundColor:
          "rgba(25,118,210,0.08) !important",
        boxShadow:
          "inset 3px 0 0 rgba(25,118,210,0.85)",
        transition: "all 120ms ease",
      }
      : {};

  const getDisplayValue = (row, field) => {
    if (isPreviewCell(row.id, field)) {
      return bulk.value;
    }
    return row?.[field];
  };

  return (
    <Grid container spacing={2}
      sx={{
        "@media print": {
          position: "relative",
          display: "block",
          width: "100%",
          verticalAlign: "top",
        },
      }}
    >
      <Grid item xs={12}
        sx={{
          "@media print": {
            position: "relative",
            display: "inline-block !important",
            width: "75%",
            verticalAlign: "top",
          },
        }}
      >
        <Box sx={{ overflowX: "auto" }}>
          <Table
            size="small"
            sx={{
              '& .MuiTableCell-root': {
                fontSize: 12,
              },
              "& tbody tr:nth-of-type(odd)": {
                backgroundColor: "rgba(0,0,0,.05)",
              },
              "@media print": {
                display: "table",
              },
            }}
          >

            <OrderTableHeader activeColumns={activeColumns} onOpenStatusManager={onOpenStatusManager} />

            <TableBody>
              {orderRows?.map(row => (
                <OrderProductRow
                  key={row.id}
                  row={row}
                  activeColumns={activeColumns}
                  isEditing={isEditing}
                  datePickerProps={datePickerProps}
                  updateField={updateField}
                  onDownloadOrderFile={onDownloadOrderFile}
                  supplierOrderStatuses={sortedSupplierOrderStatuses}
                  availableUsers={availableUsers}
                />
              ))}

              {renderSummationRows()}
            </TableBody>

          </Table>
        </Box>

        <BulkColumnPopover
          bulk={bulk}
          activeCol={activeCol}
          availableUsers={availableUsers}
          inputRef={
            activeCol
              ? inputRefs[activeCol.type]
              : null
          }
          onKeyDown={onEditorKeyDown}
          onClose={closeBulkEditor}
          onApply={applySingleBulk}
          onModeChange={(mode) =>
            setBulk((p) => ({ ...p, mode }))
          }
          onValueChange={(v) =>
            setBulk((p) => ({ ...p, value: v }))
          }
        />

        {
          /* Multi bulk edit (TODO) */
          false && (
            <Dialog
              open={multiOpen}
              onClose={() => setMultiOpen(false)}
              fullWidth
              maxWidth="sm"
            >
              <DialogTitle>Bulk edit</DialogTitle>

              <DialogContent dividers>
                <Stack spacing={1.5}>

                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={multiMode}
                    onChange={(e, v) => v && setMultiMode(v)}
                  >
                    <ToggleButton value="empty">Tomma</ToggleButton>
                    <ToggleButton value="all">Alla</ToggleButton>
                  </ToggleButtonGroup>

                  {bulkColumnDefs.map((col) => (
                    <Stack
                      key={col.field}
                      direction="row"
                      alignItems="center"
                      spacing={1}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={Boolean(multiSelected[col.field])}
                            onChange={(e) =>
                              setMultiSelected((p) => ({
                                ...p,
                                [col.field]: e.target.checked,
                              }))
                            }
                          />
                        }
                        label={col.field}
                      />

                      {col.editor?.({
                        value: multiValues[col.field],
                        setValue: (v) =>
                          setMultiValues((p) => ({
                            ...p,
                            [col.field]: v,
                          })),
                      })}
                    </Stack>
                  ))}
                </Stack>
              </DialogContent>

              <DialogActions>
                <Button onClick={() => setMultiOpen(false)}>
                  {intl.formatMessage({ id: "btn.cancel" })}
                </Button>
                <Button
                  variant="contained"
                  onClick={() => {
                    applyMultiBulk();
                    setMultiOpen(false);
                  }}
                >
                  Applicera
                </Button>
              </DialogActions>
            </Dialog>
          )
          /* (Multi bulk edit) */
        }

        <Snackbar
          open={snack.open}
          autoHideDuration={6000}
          onClose={() =>
            setSnack({ open: false, text: "" })
          }
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
        >
          <Alert
            severity="success"
            variant="filled"
            sx={{
              borderRadius: 2,
              fontSize: 13,
              boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
            }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={handleUndo}
                sx={{ fontWeight: 800 }}
              >
                �ngra
              </Button>
            }
          >
            {snack.text}
          </Alert>
        </Snackbar>

      </Grid>
    </Grid>
  );
};

OrderProductsAndSummation.propTypes = {
  isEditing: PropTypes.bool,
  onDownloadOrderFile: PropTypes.func,
  orderRows: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      amount: PropTypes.number,
      price: PropTypes.number,
      product: PropTypes.string,
      weight: PropTypes.number,
      confirmedDeliveryDate: PropTypes.oneOfType([
        PropTypes.instanceOf(Date),
        PropTypes.string,
      ]),
      deliveryDateWish: PropTypes.oneOfType([
        PropTypes.instanceOf(Date),
        PropTypes.string,
      ]),
    })
  ),
  sumExclVat: PropTypes.number,
  sumInclVat: PropTypes.number,
  showConfirmedDeliveryDateColumn: PropTypes.bool,
  showDeliveryDateWishColumn: PropTypes.bool,
  showDownloadColumn: PropTypes.bool,
  showPackageColumn: PropTypes.bool,
  showPriceColumn: PropTypes.bool,
  showProductColumn: PropTypes.bool,
  showQuantityColumn: PropTypes.bool,
  showSummation: PropTypes.bool,
  showWeightColumn: PropTypes.bool,
  transportFee: PropTypes.number,
  vatAmounts: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      vatAmount: PropTypes.number.isRequired,
      vatPercentage: PropTypes.number,
    })
  ),
};

OrderProductsAndSummation.displayName = "OrderProductsAndSummation";

export default OrderProductsAndSummation;
